import React, { useState, useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import { useMediaDevices } from "../../hooks/useMediaDevices";
import { useMicrophoneTest } from "../../hooks/useMicrophoneTest";
import { getPreferredMic } from "../../audio-kernel/micPreference";
import MicCheckStep from "../Instructions/steps/MicCheckStep";

const UnifiedResumeScreen = ({
  resumeType = "normal", // "normal" | "technical"
  onResumeComplete,
  candidateName = "Candidate",
  questionNumber = null,
  interview_id = null,
}) => {
  // Technical = mic died mid-interview; goes straight to the mic test.
  // Normal = show the "welcome back" card first, candidate opts in.
  const [showMicTest, setShowMicTest] = useState(resumeType === "technical");
  const [showMicHelp, setShowMicHelp] = useState(false);
  const { theme } = useTheme();

  // Same kernel-backed test used on the instructions page: device picker,
  // aliveness gate, RMS detection, SIGNAL_DEAD blocking screen — instead of
  // a second hand-rolled detector that would drift from it over time.
  const [errorMessage, setErrorMessage] = useState(null);
  const noop = () => {};
  const devices = useMediaDevices({ setCameraStatus: noop, setErrorMessage });
  const {
    selectedMic,
    setSelectedMic,
    availableMics,
    permissionsStatus,
    setPermissionsStatus,
    isRequestingMic,
    setIsRequestingMic,
    getOrCreateStream,
    checkAndEnumerateDevices,
    requestPermissions,
    stopAllStreams,
  } = devices;

  // Mount check: this screen appears mid-interview (the candidate already
  // granted mic permission once) — enumerate + auto-select immediately
  // instead of showing a redundant "Grant Permission" button first.
  //
  // devicechange listener: without this, plugging in a new mic while
  // already on this screen never updates the picker — only a page refresh
  // (which re-enumerates from scratch) would show it. Same pattern as
  // InterviewInstructions' mount-only effect (empty deps — checkAndEnumerateDevices
  // is stable per hook instance, so this never re-fires and can't reintroduce
  // the P0 effect-race that caused retry storms there).
  useEffect(() => {
    requestPermissions();
    navigator.mediaDevices.addEventListener("devicechange", checkAndEnumerateDevices);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", checkAndEnumerateDevices);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-shot restore of the device that was working before the trip —
  // same pattern as InterviewInstructions, never re-applied so it can't
  // stomp a manual dropdown choice.
  const restoredPreferredMicRef = useRef(false);
  useEffect(() => {
    if (restoredPreferredMicRef.current) return;
    if (permissionsStatus.microphone !== "granted") return;
    if (!availableMics.length) return;
    restoredPreferredMicRef.current = true;
    const pref = getPreferredMic(interview_id);
    if (pref?.deviceId && availableMics.some((m) => m.deviceId === pref.deviceId)) {
      setSelectedMic(pref.deviceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsStatus.microphone, availableMics]);

  const mic = useMicrophoneTest({
    interviewId: interview_id,
    selectedMic,
    setSelectedMic,
    availableMics,
    permissionsStatus,
    setPermissionsStatus,
    setErrorMessage,
    stopAllStreams,
  });
  const {
    micStatus,
    setMicStatus,
    isTestingMic,
    liveLevel,
    lastVerdict,
    testedDeviceLabel,
    testPhase,
    testMicrophone,
  } = mic;

  // Release the test's stream/context if this screen unmounts mid-test
  // (mirrors InterviewInstructions' shell cleanup; logs ABORTED for a run
  // killed in flight). Safe to double-run — the pass path already released.
  useEffect(() => {
    return () => {
      mic.cleanupAudio();
      stopAllStreams();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestMicPermission = async () => {
    setIsRequestingMic(true);
    try {
      await getOrCreateStream("audio", selectedMic);
      await checkAndEnumerateDevices();
    } catch (error) {
      console.error("Error requesting microphone permission:", error);
      setMicStatus("failed");
      let msg = "";
      if (error.name === "NotAllowedError" || error.name === "SecurityError") {
        setPermissionsStatus((prev) => ({ ...prev, microphone: "denied" }));
        msg = "Microphone permission denied. Please enable microphone access in your browser settings or try again.";
      } else if (error.name === "NotFoundError") {
        msg = "No microphone found. Please connect a microphone and refresh the page.";
      } else if (error.name === "NotReadableError") {
        msg = "Your microphone is in use by another app (like Zoom or Teams). Please close it and try again.";
      } else {
        msg = "Error accessing microphone. Please check your settings and try again.";
      }
      setErrorMessage(msg);
    } finally {
      setIsRequestingMic(false);
    }
  };

  const handleStartMicTest = () => setShowMicTest(true);

  const handleContinue = () => {
    if (onResumeComplete) onResumeComplete(true);
  };

  const getContent = () => {
    if (resumeType === "technical") {
      return {
        title: "Microphone Check Required",
        subtitle: "Audio Quality Issue Detected",
        message:
          "We detected audio issues during your interview. Let's test your microphone before continuing.",
        progressText: questionNumber ? `Question ${questionNumber}` : "Interview in Progress",
      };
    }
    return {
      title: "Welcome Back!",
      subtitle: "Resume Your Interview",
      message:
        "We've saved your progress from your previous session. Let's test your microphone and continue your journey.",
      progressText: "Interview in Progress",
    };
  };

  const content = getContent();

  if (showMicTest) {
    return (
      <div className="w-full max-w-xl sm:max-w-2xl mx-auto px-4 overflow-hidden">
        <div className="text-center mb-4">
          <h2
            className={`text-2xl font-bold ${
              theme === "light"
                ? "text-textPrimary"
                : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] bg-clip-text text-transparent"
            }`}
          >
            {content.title}
          </h2>
          <p className="text-gray-400 text-sm py-2">{content.subtitle}</p>
          <div
            className={`w-16 h-1 mx-auto rounded-full ${
              theme === "light" ? "bg-gray-600" : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE]"
            }`}
          />
        </div>

        <MicCheckStep
          showMicHelp={showMicHelp}
          setShowMicHelp={setShowMicHelp}
          setErrorMessage={setErrorMessage}
          errorMessage={errorMessage}
          setMicStatus={setMicStatus}
          micStatus={micStatus}
          testMicrophone={testMicrophone}
          permissionsStatus={permissionsStatus}
          requestMicPermission={requestMicPermission}
          isRequestingMic={isRequestingMic}
          selectedMic={selectedMic}
          setSelectedMic={setSelectedMic}
          availableMics={availableMics}
          isTestingMic={isTestingMic}
          liveLevel={liveLevel}
          lastVerdict={lastVerdict}
          testedDeviceLabel={testedDeviceLabel}
          testPhase={testPhase}
          handleNextStep={handleContinue}
          showHeader={false}
          continueLabel="Continue"
          speakPromptText='Please speak clearly: "I am ready to continue the interview"'
        />
      </div>
    );
  }

  // Normal resume screen (before mic test) — session-resume entry card,
  // unrelated to the mid-interview mic-death path above.
  return (
    <div className="w-full max-w-lg mx-auto ">
      <div className="relative space-y-8 ">
        <div className="space-y-2 text-center">
          <h2
            className={`text-2xl font-bold ${
              theme === "light"
                ? "text-gray-700"
                : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] bg-clip-text text-transparent"
            }`}
          >
            {content.title}
          </h2>
          <div
            className={`w-16 h-1 mx-auto rounded-full ${
              theme === "light" ? "bg-gray-600" : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE]"
            }`}
          />
        </div>

        <div className="space-y-4">
          <p
            className={` text-lg text-center leading-relaxed ${
              theme === "light" ? "text-gray-700" : "text-gray-300"
            }`}
          >
            {content.message}
          </p>
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-300">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-gray-600">{content.progressText}</span>
          </div>
        </div>

        <div className="pt-4 space-y-3">
          <div className="flex items-center justify-center w-full">
            <button
              onClick={handleStartMicTest}
              className={`group relative w-full max-w-xs button bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] text-white
                `}
            >
              <div className="relative flex items-center justify-center space-x-2">
                <span>Test Microphone & Resume </span>
                <ArrowRight className="w-5 h-5 transform transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedResumeScreen;
